import React from 'react';
import { Check, Loader2 } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FormEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalizedPath } from '../utils/navigation';
import { useLocalizedNavigate } from '../hooks/useLocalizedNavigate';
import { getPricingDefaultValue } from '../utils/pricingI18n';
import { auth, db, appCheck } from '../firebaseConfig';
import { doc, getDoc } from 'firebase/firestore';
import { getToken } from 'firebase/app-check';
import { Elements, PaymentElement, ExpressCheckoutElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { trackEvent, trackPurchase, trackBeginCheckout } from '../services/analytics';
import {
  createSubscription,
  verifyPayment,
  getSubscriptionStatus,
  createOrder,
  verifyOrder,
  changePlan,
  syncSubscription,
  getBillingGateway,
  stripeCreateSubscriptionIntent,
  stripeCreateTopUpIntent,
  stripeChangePlan,
} from '../services';
import { useTheme } from './ThemeProvider';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

interface Plan {
  name: string;
  monthlyPrice: string;
  yearlyPrice: string;
  period: string;
  features: string[];
  monthlyPlanId?: string;
  yearlyPlanId?: string;
  popular?: boolean;
}

interface RazorpayOptions {
  key: string;
  subscription_id?: string;
  order_id?: string;
  amount: number;
  currency: string;
  name: string;
  description: string;
  handler: (response: {
    razorpay_payment_id: string;
    razorpay_subscription_id?: string;
    razorpay_order_id?: string;
    razorpay_signature: string;
  }) => void;
  theme: { color: string };
  modal: { ondismiss: () => void };
  retry: { enabled: boolean; max_count: number };
}

interface RazorpayInstance {
  open: () => void;
  on: (event: string, callback: (response: any) => void) => void;
}

declare global {
  interface Window {
    Razorpay: new (options: RazorpayOptions) => RazorpayInstance;
  }
}

// Purchase event now handled by analytics.trackPurchase() — unified pipeline with
// consent checks, deduplication, and retry logic built in.

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string);

type StripePaymentStatus = 'succeeded' | 'processing' | string;
type StripePaymentFormResult =
  | { ok: false; message?: string }
  | { ok: true; status: StripePaymentStatus; paymentIntentId?: string };

interface MissionBadgeCompactProps {
  className?: string;
  title?: string;
  description?: string;
}

const SealMark = ({ size = 28, className = '' }: { size?: number; className?: string }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" fill="none" className={className} aria-hidden="true">
    <circle cx="24" cy="24" r="22" stroke="currentColor" strokeWidth="1.5" opacity="0.3" />
    <circle cx="24" cy="24" r="18" stroke="currentColor" strokeWidth="1" opacity="0.2" />
    <path
      d="M16 16v16c0 0 3-2 8-2s8 2 8 2V16c0 0-3-2-8-2s-8 2-8 2z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
      fill="currentColor"
      fillOpacity="0.08"
    />
    <path d="M24 14v18" stroke="currentColor" strokeWidth="1" opacity="0.5" />
    {Array.from({ length: 12 }).map((_, i) => {
      const angle = (i * 30 * Math.PI) / 180;
      const cx = 24 + 20.5 * Math.cos(angle);
      const cy = 24 + 20.5 * Math.sin(angle);
      return <circle key={i} cx={cx} cy={cy} r="0.8" fill="currentColor" opacity="0.25" />;
    })}
  </svg>
);

const MissionBadgeCompact: React.FC<MissionBadgeCompactProps> = ({ className = '', title, description }) => (
  <div className={`mx-auto max-w-2xl ${className}`}>
    <div className="border-t border-b border-gray-300 py-4 flex flex-col sm:flex-row items-center justify-center gap-4">
      <SealMark size={28} className="text-gray-700 flex-shrink-0" />
      <div className="hidden sm:block h-6 w-px bg-gray-300" />
      <div className="min-w-0 text-center sm:text-left">
        <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-gray-500 block">
          {title || 'Mission Driven'}
        </span>
        <p className="text-[13px] text-gray-700 mt-0.5">{description || 'Every plan funds the digitization of ancient manuscripts.'}</p>
      </div>
    </div>
  </div>
);

function StripePaymentForm({
  clientSecret,
  onDone,
  planName,
  billingPeriod,
  deviceType,
  flowType,
  purchaseCurrency,
  purchaseValue,
  userEmail,
}: {
  clientSecret: string;
  onDone: (result: StripePaymentFormResult) => void;
  planName: string;
  billingPeriod: 'monthly' | 'annual';
  deviceType: 'mobile' | 'desktop';
  flowType: 'subscribe' | 'topup' | 'plan_change';
  purchaseCurrency: string;
  purchaseValue: number | undefined;
  userEmail?: string;
}) {
  const { t } = useTranslation('planDetails');
  const stripe = useStripe();
  const elements = useElements();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [showExpressCheckout, setShowExpressCheckout] = useState(false);
  const hasStartedRef = useRef(false);
  const didSubmitRef = useRef(false);
  const didSucceedRef = useRef(false);

  useEffect(() => {
    trackEvent('checkout_viewed', {
      category: 'Checkout',
      gateway: 'stripe',
      plan_name: planName,
      billing_period: billingPeriod,
      device_type: deviceType,
      flow_type: flowType,
    });

    return () => {
      if (!didSubmitRef.current && !didSucceedRef.current) {
        trackEvent('checkout_abandoned', {
          category: 'Checkout',
          gateway: 'stripe',
          plan_name: planName,
          billing_period: billingPeriod,
          device_type: deviceType,
          flow_type: flowType,
        });
      }
    };
  }, [billingPeriod, deviceType, flowType, planName]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) {
      setFormError(t('planDetails.checkout_form_loading'));
      return;
    }

    setFormError(null);
    setIsSubmitting(true);
    didSubmitRef.current = true;
    trackEvent('payment_confirm_clicked', {
      category: 'Checkout',
      gateway: 'stripe',
      plan_name: planName,
      billing_period: billingPeriod,
      device_type: deviceType,
      flow_type: flowType,
    });
    try {
      localStorage.setItem(
        'sarvalok_last_purchase',
        JSON.stringify({
          gateway: 'stripe',
          flow_type: flowType,
          plan_name: planName,
          billing_period: billingPeriod,
          currency: purchaseCurrency,
          value: purchaseValue,
          ts: Date.now(),
        })
      );
    } catch {}
    const detectedLang = window.location.pathname.match(/^\/([a-z]{2})(\/|$)/)?.[1] || 'en';
    const returnUrl = `${window.location.origin}/${detectedLang}/payment-success?gateway=stripe&flow=${encodeURIComponent(
      flowType
    )}&plan=${encodeURIComponent(planName)}&billing=${encodeURIComponent(billingPeriod)}`;
    const result = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: returnUrl,
      },
      redirect: 'if_required',
    });

    if (result.error) {
      const message = result.error.message || 'Payment failed';
      setFormError(message);
      trackEvent('payment_error', {
        category: 'Checkout',
        gateway: 'stripe',
        plan_name: planName,
        billing_period: billingPeriod,
        device_type: deviceType,
        flow_type: flowType,
        error_message: message,
      });
      onDone({ ok: false, message: result.error.message || 'Payment failed' });
      setIsSubmitting(false);
      return;
    }

    const paymentStatus: StripePaymentStatus = result.paymentIntent?.status || 'succeeded';
    const paymentIntentId = result.paymentIntent?.id || '';
    if (paymentStatus !== 'succeeded' && paymentStatus !== 'processing') {
      const message = t('planDetails.payment_not_complete', {
        defaultValue: 'Payment is not complete yet. Please finish authorization or try another payment method.',
      });
      setFormError(message);
      trackEvent('payment_incomplete', {
        category: 'Checkout',
        gateway: 'stripe',
        plan_name: planName,
        billing_period: billingPeriod,
        device_type: deviceType,
        flow_type: flowType,
        payment_status: paymentStatus,
      });
      onDone({ ok: false, message });
      setIsSubmitting(false);
      return;
    }

    didSucceedRef.current = true;
    if (paymentIntentId) {
      try {
        const raw = localStorage.getItem('sarvalok_last_purchase');
        if (raw) {
          const existing = JSON.parse(raw);
          localStorage.setItem(
            'sarvalok_last_purchase',
            JSON.stringify({
              ...existing,
              gateway: 'stripe',
              transaction_id: paymentIntentId,
              payment_status: paymentStatus,
            })
          );
        }
      } catch {}
    }
    if (paymentIntentId && paymentStatus === 'succeeded') {
      trackPurchase({
        gateway: 'stripe',
        transactionId: paymentIntentId,
        currency: purchaseCurrency,
        value: purchaseValue,
        planName,
        billingPeriod,
        flowType,
      });
    }
    trackEvent(paymentStatus === 'processing' ? 'payment_processing' : 'payment_success', {
      category: 'Checkout',
      gateway: 'stripe',
      plan_name: planName,
      billing_period: billingPeriod,
      device_type: deviceType,
      flow_type: flowType,
      payment_status: paymentStatus,
    });
    onDone({ ok: true, status: paymentStatus, paymentIntentId });
    setIsSubmitting(false);
  };

  const handleExpressCheckoutConfirm = async () => {
    if (!stripe || !elements) return;

    setFormError(null);
    setIsSubmitting(true);
    didSubmitRef.current = true;

    trackEvent('payment_confirm_clicked', {
      category: 'Checkout',
      gateway: 'stripe',
      plan_name: planName,
      billing_period: billingPeriod,
      device_type: deviceType,
      flow_type: flowType,
      method: 'express_checkout',
    });

    try {
      localStorage.setItem(
        'sarvalok_last_purchase',
        JSON.stringify({
          gateway: 'stripe',
          flow_type: flowType,
          plan_name: planName,
          billing_period: billingPeriod,
          currency: purchaseCurrency,
          value: purchaseValue,
          ts: Date.now(),
        })
      );
    } catch {}

    const detectedLang = window.location.pathname.match(/^\/([a-z]{2})(\/|$)/)?.[1] || 'en';
    const returnUrl = `${window.location.origin}/${detectedLang}/payment-success?gateway=stripe&flow=${encodeURIComponent(
      flowType
    )}&plan=${encodeURIComponent(planName)}&billing=${encodeURIComponent(billingPeriod)}`;

    const result = await stripe.confirmPayment({
      elements,
      clientSecret,
      confirmParams: {
        return_url: returnUrl,
      },
      redirect: 'if_required',
    });

    if (result.error) {
      const message = result.error.message || 'Payment failed';
      setFormError(message);
      trackEvent('payment_error', {
        category: 'Checkout',
        gateway: 'stripe',
        plan_name: planName,
        billing_period: billingPeriod,
        device_type: deviceType,
        flow_type: flowType,
        method: 'express_checkout',
        error_message: message,
      });
      onDone({ ok: false, message });
      setIsSubmitting(false);
      return;
    }

    const paymentStatus: StripePaymentStatus = result.paymentIntent?.status || 'succeeded';
    const paymentIntentId = result.paymentIntent?.id || '';
    if (paymentStatus !== 'succeeded' && paymentStatus !== 'processing') {
      const message = t('planDetails.payment_not_complete', {
        defaultValue: 'Payment is not complete yet. Please finish authorization or try another payment method.',
      });
      setFormError(message);
      trackEvent('payment_incomplete', {
        category: 'Checkout',
        gateway: 'stripe',
        plan_name: planName,
        billing_period: billingPeriod,
        device_type: deviceType,
        flow_type: flowType,
        method: 'express_checkout',
        payment_status: paymentStatus,
      });
      onDone({ ok: false, message });
      setIsSubmitting(false);
      return;
    }

    didSucceedRef.current = true;
    if (paymentIntentId) {
      try {
        const raw = localStorage.getItem('sarvalok_last_purchase');
        if (raw) {
          const existing = JSON.parse(raw);
          localStorage.setItem(
            'sarvalok_last_purchase',
            JSON.stringify({
              ...existing,
              gateway: 'stripe',
              transaction_id: paymentIntentId,
              payment_status: paymentStatus,
            })
          );
        }
      } catch {}
    }
    if (paymentIntentId && paymentStatus === 'succeeded') {
      trackPurchase({
        gateway: 'stripe',
        transactionId: paymentIntentId,
        currency: purchaseCurrency,
        value: purchaseValue,
        planName,
        billingPeriod,
        flowType,
      });
    }
    trackEvent(paymentStatus === 'processing' ? 'payment_processing' : 'payment_success', {
      category: 'Checkout',
      gateway: 'stripe',
      plan_name: planName,
      billing_period: billingPeriod,
      device_type: deviceType,
      flow_type: flowType,
      method: 'express_checkout',
      payment_status: paymentStatus,
    });
    onDone({ ok: true, status: paymentStatus, paymentIntentId });
    setIsSubmitting(false);
  };

  return (
    <form onSubmit={handleSubmit} className="w-full">
      <div className="flex items-center gap-2 mb-2">
        <img src="/logo.png" alt="Sarvalok" className="w-7 h-auto object-contain flex-shrink-0" />
        <span
          className="text-[18px] leading-none text-gray-900 dark:text-gray-100"
          style={{ fontFamily: "'General Sans', 'Inter', 'Segoe UI', -apple-system, sans-serif", fontWeight: 600, marginLeft: '-2px' }}
        >
          Sarvalok
        </span>
      </div>
      <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] text-gray-500 dark:text-gray-400 mb-3">
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          className="text-gray-400 dark:text-gray-500 flex-shrink-0"
          aria-hidden="true"
        >
          <path
            d="M12 2L4 6v5c0 5.25 3.4 10.15 8 11.4 4.6-1.25 8-6.15 8-11.4V6l-8-4z"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinejoin="round"
            fill="currentColor"
            fillOpacity="0.08"
          />
          <path
            d="M9 12.5l2 2 4-4.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <span>{t('planDetails.checkout_processed_securely')}</span>
        <span aria-hidden="true">•</span>
        <span>Sarvalok Inc., USA</span>
        <span aria-hidden="true">•</span>
        <span className="min-w-0">
          {t('planDetails.checkout_need_help')}{' '}
          <a className="underline underline-offset-2 hover:text-gray-700 dark:hover:text-gray-300" href="mailto:support@sarvalok.com">
            support@sarvalok.com
          </a>
        </span>
      </p>
      {/* Express Checkout: Link, Apple Pay, Google Pay */}
      <div className={showExpressCheckout ? 'mb-4' : ''}>
        <ExpressCheckoutElement
          onConfirm={handleExpressCheckoutConfirm}
          onReady={({ availablePaymentMethods }) => {
            if (availablePaymentMethods) {
              const hasMethod = Object.values(availablePaymentMethods).some(Boolean);
              setShowExpressCheckout(hasMethod);
            }
          }}
          onClick={({ resolve }) => {
            if (!hasStartedRef.current) {
              hasStartedRef.current = true;
              trackEvent('checkout_started', {
                category: 'Checkout',
                gateway: 'stripe',
                plan_name: planName,
                billing_period: billingPeriod,
                device_type: deviceType,
                flow_type: flowType,
                method: 'express_checkout',
              });
            }
            resolve();
          }}
          options={{
            paymentMethods: {
              applePay: 'auto',
              googlePay: 'auto',
              link: 'auto',
            },
            buttonHeight: 48,
            buttonType: {
              applePay: 'plain',
              googlePay: 'plain',
              link: 'auto',
            },
            layout: {
              maxColumns: 2,
              maxRows: 1,
              overflow: 'never',
            },
          }}
        />
      </div>
      {/* Divider — only when express checkout buttons are visible */}
      {showExpressCheckout && (
        <div className="flex items-center gap-3 mb-4">
          <div className="flex-1 border-t border-gray-200 dark:border-gray-600" />
          <span className="text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap">
            {t('planDetails.checkout_or_pay_with_card')}
          </span>
          <div className="flex-1 border-t border-gray-200 dark:border-gray-600" />
        </div>
      )}
      <div className="border border-gray-200 dark:border-gray-600 rounded-xl p-5 mb-4 shadow-sm dark:bg-gray-800">
        <PaymentElement
          onChange={() => {
            if (hasStartedRef.current) return;
            hasStartedRef.current = true;
            trackEvent('checkout_started', {
              category: 'Checkout',
              gateway: 'stripe',
              plan_name: planName,
              billing_period: billingPeriod,
              device_type: deviceType,
              flow_type: flowType,
            });
          }}
          options={{
            ...(userEmail ? {
              defaultValues: {
                billingDetails: {
                  email: userEmail,
                },
              },
            } : {}),
            terms: { card: 'never', usBankAccount: 'never' },
          }}
        />
        <p className="mt-3 text-[11px] leading-relaxed text-gray-400 dark:text-gray-500">
          By providing your payment information, you authorize Sarvalok Inc. to charge you for payments, including recurring subscriptions where applicable, in accordance with our{' '}
          <a href="/terms" className="underline underline-offset-2 hover:text-gray-500 dark:hover:text-gray-400">Terms of Service</a>.
        </p>
        <div className="mt-2 flex justify-end">
          <a href="https://stripe.com" target="_blank" rel="noopener noreferrer" className="opacity-40 hover:opacity-60 transition-opacity">
            <img src="/powered-by-stripe.svg" alt="Powered by Stripe" className="h-5 dark:hidden" />
            <img src="/powered-by-stripe-white.svg" alt="Powered by Stripe" className="h-5 hidden dark:block" />
          </a>
        </div>
      </div>
      <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-gray-500 dark:text-gray-400 mb-4">
        <span>{t('planDetails.checkout_encrypted')}</span>
        <span aria-hidden="true">•</span>
        <span>{t('planDetails.checkout_no_card_stored')}</span>
        <span aria-hidden="true">•</span>
        <span>{t('planDetails.checkout_instant_activation')}</span>
      </p>
      <button
        type="submit"
        disabled={!clientSecret || !stripe || !elements || isSubmitting}
        className={`w-full py-3 px-6 rounded-lg font-medium transition flex items-center justify-center ${
          !clientSecret || !stripe || !elements || isSubmitting
            ? 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
            : 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800'
        }`}
      >
        {isSubmitting ? (
          <>
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            {t('planDetails.checkout_processing')}
          </>
        ) : (
          t('planDetails.checkout_confirm_payment')
        )}
      </button>
      {formError ? (
        <p role="alert" className="mt-3 text-[12px] text-gray-700 dark:text-gray-300">
          {t('planDetails.checkout_payment_error')}: {formError}
        </p>
      ) : null}
    </form>
  );
}

export default function PlanDetailsPage({curren,multiplier}:any) {
  // Bind translations to the 'planDetails' namespace to resolve keys like 'planDetails.*'
  const { t, i18n } = useTranslation('planDetails');
  const { t: tPricing } = useTranslation('pricing');
  const tp = (key: string, options?: Record<string, unknown>) =>
    tPricing(key, { defaultValue: getPricingDefaultValue(key), ...(options ?? {}) });
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const location = useLocation();
  const navigate = useLocalizedNavigate();
  const localizedPath = useLocalizedPath();

  // Safely read navigation state and provide resilient fallbacks (localStorage)
  const navState = (location?.state as { plan: Plan; isAnnual: boolean } | null) ?? null;
  const [plan, setPlan] = useState<Plan | null>(navState?.plan ?? null);
  const [isAnnual, setIsAnnual] = useState<boolean>(navState?.isAnnual ?? false);
  const [isRazorpayLoaded, setIsRazorpayLoaded] = useState(false);
  const [isPaymentLoading, setIsPaymentLoading] = useState(false);
  const [currencyState, setCurrency] = useState<string>('USD');
  const [currencyMultiplier, setCurrencyMultiplier] = useState<number>(1);
  const [stripeClientSecret, setStripeClientSecret] = useState<string | null>(null);
  const [stripeSuccessMessage, setStripeSuccessMessage] = useState<string | null>(null);
  const stripeFormRef = useRef<HTMLDivElement>(null);
  const [userDetails, setUserDetails] = useState<{
    name: string;
    email: string;
    contact: string;
  }>({
    name: '',
    email: '',
    contact: '',
  });
  const [currentSubscription, setCurrentSubscription] = useState<{
    subscriptionId: string | null;
    planName: string | null;
    period: number | null;
    date: Date | null;
    isExpired: boolean;
  }>({
    subscriptionId: null,
    planName: null,
    period: null,
    date: null,
    isExpired: false,
  });
  const user = auth.currentUser;
  const userUid = user?.uid || '';

  const formatHits = (hits: number) => {
    try {
      return new Intl.NumberFormat(i18n.language).format(hits);
    } catch {
      return hits.toLocaleString();
    }
  };

  const stripeTopupHits = 6000;
  const razorpayTopupHits = 1800;
  const preferredGateway =
    currencyState === 'INR' ? 'razorpay' : currencyState === 'USD' ? 'stripe' : null;

  const getDynamicTokenFeature = (planName: string) => {
    if (planName === 'Explorer') {
      return currencyState === 'INR'
        ? isAnnual
          ? tp('pricing.token_labels.total_output_tokens_per_year', { count: '43,200', perMonth: '3,600' })
          : tp('pricing.token_labels.total_output_tokens_per_month', { count: '3,600' })
        : isAnnual
          ? tp('pricing.token_labels.total_output_tokens_per_year', { count: '150,000', perMonth: '12,500' })
          : tp('pricing.token_labels.total_output_tokens_per_month', { count: '12,500' });
    }

    if (planName === 'Scholar') {
      return currencyState === 'INR'
        ? isAnnual
          ? tp('pricing.token_labels.total_output_tokens_per_year', { count: '86,400', perMonth: '7,200' })
          : tp('pricing.token_labels.total_output_tokens_per_month', { count: '7,200' })
        : isAnnual
          ? tp('pricing.token_labels.total_output_tokens_per_year', { count: '300,000', perMonth: '25,000' })
          : tp('pricing.token_labels.total_output_tokens_per_month', { count: '25,000' });
    }

    if (planName === 'Sage') {
      return currencyState === 'INR'
        ? isAnnual
          ? tp('pricing.token_labels.total_output_tokens_per_year', { count: '172,800', perMonth: '14,400' })
          : tp('pricing.token_labels.total_output_tokens_per_month', { count: '14,400' })
        : isAnnual
          ? tp('pricing.token_labels.total_output_tokens_per_year', { count: '600,000', perMonth: '50,000' })
          : tp('pricing.token_labels.total_output_tokens_per_month', { count: '50,000' });
    }

    if (planName === 'Top-Up') {
      return tp('pricing.token_labels.add_output_tokens_one_time', {
        count: currencyState === 'INR' ? '1,800' : '6,000',
      });
    }

    return null;
  };

  const getPlanDisplayPrice = (selectedPlan: Plan) =>
    selectedPlan.name === 'Enlightened'
      ? tp('pricing.custom_pricing')
      : isAnnual
        ? selectedPlan.yearlyPrice
        : selectedPlan.monthlyPrice;

  const getPlanDisplayPeriod = (planName: string) => {
    if (planName === 'Enlightened') {
      return '';
    }

    if (planName === 'Top-Up') {
      return tp('pricing.one_time');
    }

    return isAnnual ? t('planDetails.per_year') : t('planDetails.per_month');
  };

  // Helper function to get translated features for each plan
  const getTranslatedFeatures = (planName: string) => {
    const planKey = planName === 'free_plan' ? 'free' : planName.toLowerCase();
    const featuresKey = `${planKey}_features`;
    
    if (planName === 'Top-Up') {
      const translatedTopUp = getDynamicTokenFeature(planName);
      return translatedTopUp ? [translatedTopUp] : [];
    }
    
    const features: string[] = [];
    for (let i = 1; i <= 9; i++) {
      const featureKey = `pricing.${featuresKey}.feature${i}`;
      try {
        const feature = tp(featureKey);
        if (feature !== featureKey) { // Only add if translation exists
          features.push(feature);
        }
      } catch (e) {
        break; // Stop if no more features
      }
    }

    const dynamicTokenFeature = getDynamicTokenFeature(planName);
    if (features.length && dynamicTokenFeature) {
      features[0] = dynamicTokenFeature;
    }
    if (currencyState === 'INR') {
      return features.map((feature) =>
        /AI Studio/i.test(feature)
          ? t('pricing.explorer_features.feature_day_quality', { defaultValue: 'Day Quality & Muhurta timeline' })
          : feature
      );
    }
    return features;
  };

  // Attempt to recover from direct navigation or language changes by reading localStorage
  useEffect(() => {
    if (!plan) {
      try {
        const saved = localStorage.getItem('plan');
        if (saved) {
          const parsed = JSON.parse(saved);
          if (parsed?.plan) {
            setPlan(parsed.plan);
            setIsAnnual(Boolean(parsed.isAnnual));
          }
        }
      } catch (e) {
        console.error('Failed to parse saved plan from localStorage:', e);
      }
    }
  }, [plan]);

  // --- Logic Flags (safe when plan is defined) ---
  const isPlanNameSame = plan ? currentSubscription.planName === plan.name : false;
  const isPeriodDifferent = plan ? isAnnual !== (currentSubscription.period === 12) : false;

  const isSwitchingToAnnual = isPlanNameSame && isPeriodDifferent && isAnnual;
  const isSwitchingToMonthly = isPlanNameSame && isPeriodDifferent && !isAnnual;

  // Guard: show loading until plan context is available
  if (!plan) {
    return (
      <div className="bg-white py-24">
        <div className="container mx-auto px-6 text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4" />
          <p>{t('planDetails.loading_plan_details')}</p>
        </div>
      </div>
    );
  }

  useEffect(() => {
    try {
      if(curren && multiplier){
        setCurrency(curren);
        const multipliers = parseFloat(multiplier);
        if (!isNaN(multipliers)) {
          setCurrencyMultiplier(multipliers);
        }
      } else {
        const savedCurrency = localStorage.getItem('currency');
        const savedMultiplier = localStorage.getItem('currencyMultiplier');
        if (savedCurrency) {
          setCurrency(savedCurrency);
        }
        if (savedMultiplier) {
          const multiplierValue = parseFloat(savedMultiplier);
          if (!isNaN(multiplierValue)) {
            setCurrencyMultiplier(multiplierValue);
          }
        }
      }
    } catch (error) {
      console.error('Error reading currency from localStorage:', error);
    }
  }, [curren,multiplier]);

  useEffect(() => {
    const loadRazorpayScript = () => {
      return new Promise<void>((resolve, reject) => {
        if (window.Razorpay) {
          resolve();
          return;
        }
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('Failed to load Razorpay SDK'));
        document.body.appendChild(script);
      });
    };

    loadRazorpayScript()
      .then(() => setIsRazorpayLoaded(true))
      .catch((error) => {
        console.error(error);
        toast.error('Failed to load Razorpay SDK');
      });

    if (user) {
      setUserDetails({
        name: user.displayName || 'User',
        email: user.email || '',
        contact: user.phoneNumber || '',
      });

      const fetchSubscriptionData = async () => {
        try {
          // Ensure App Check token is available before Firestore operations
          if (appCheck) {
            try {
              await getToken(appCheck);
              console.log("✅ App Check token verified for PlansDetailPage Firestore operations");
            } catch (appCheckError) {
              console.error("❌ App Check token verification failed in PlansDetailPage:", appCheckError);
              // Continue anyway in development, but log the error
              if (!import.meta.env.DEV) {
                throw appCheckError;
              }
            }
          }
          
          const userDocRef = doc(db, 'users', userUid);
          const userDoc = await getDoc(userDocRef);
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const subscriptionId = userData.subscription_id || null;
            const planName = userData.subscription_name || null;
            const period = userData.period || null;
            const subscriptionDate = userData.date ? new Date(userData.date) : null;

            const daysDiff = subscriptionDate
              ? Math.floor((new Date().getTime() - subscriptionDate.getTime()) / (1000 * 60 * 60 * 24))
              : 0;
            const isExpired = (period === 1 && daysDiff > 30) || (period === 12 && daysDiff > 365);

            setCurrentSubscription({
              subscriptionId,
              planName,
              period,
              date: subscriptionDate,
              isExpired,
            });

            // await syncSubscription(userUid); // Consider if this is needed on page load or only after payment
            const updatedDoc = await getDoc(userDocRef);
            const updatedData = updatedDoc.data();
            setCurrentSubscription({
              subscriptionId: updatedData?.subscription_id || null,
              planName: updatedData?.subscription_name || null,
              period: updatedData?.period || null,
              date: updatedData?.date ? new Date(updatedData.date) : null,
              isExpired: !updatedData?.active || false,
            });
          }
        } catch (error) {
          console.error('Error fetching subscription data:', error);
          toast.error(t('planDetails.fetch_subscription_failed'));
        }
      };

      fetchSubscriptionData();
    }

    return () => {
      const script = document.querySelector('script[src="https://checkout.razorpay.com/v1/checkout.js"]');
      if (script) document.body.removeChild(script);
    };
  }, [t, user, userUid]);

  const handlePayment = async () => {
    if (!userUid) {
      navigate('/login');
      return;
    }

    setIsPaymentLoading(true);
    setStripeClientSecret(null);
    setStripeSuccessMessage(null);

    const getValueFromMinorUnits = (amount: number | undefined | null) => {
      if (typeof amount !== 'number' || !Number.isFinite(amount)) return undefined;
      return amount / 100;
    };

    try {
      const billing = await getBillingGateway();
      const gateway = preferredGateway || billing?.gateway || 'stripe';

      if (plan.name !== 'free_plan') {
        const rawPrice = plan.name === 'Top-Up' ? plan.monthlyPrice : isAnnual ? plan.yearlyPrice : plan.monthlyPrice;
        const normalized = String(rawPrice || '')
          .replace(/[^0-9.,-]/g, '')
          .replace(/,/g, '');
        const value = Number.parseFloat(normalized);
        trackBeginCheckout({
          gateway,
          currency: typeof currencyState === 'string' ? currencyState : undefined,
          value: Number.isFinite(value) && value > 0 ? value : undefined,
          planName: plan.name,
          billingPeriod,
          flowType,
        });
      }

      if (gateway === 'stripe') {
        if (plan.name === 'Top-Up') {
          const intent = await stripeCreateTopUpIntent({ uid: userUid });
          setStripeClientSecret(intent.client_secret);
          setStripeSuccessMessage(t('planDetails.topup_success', { hits: formatHits(stripeTopupHits) }));
          setIsPaymentLoading(false);
          return;
        }

        if (
          !currentSubscription.subscriptionId ||
          currentSubscription.isExpired ||
          currentSubscription.planName === 'free_plan'
        ) {
          const intent = await stripeCreateSubscriptionIntent({
            uid: userUid,
            plan_name: plan.name,
            is_annual: isAnnual,
          });
          setStripeClientSecret(intent.client_secret);
          setStripeSuccessMessage(t('planDetails.subscription_success'));
          setIsPaymentLoading(false);
          return;
        }

        if (currentSubscription.planName !== plan.name || currentSubscription.period !== (isAnnual ? 12 : 1)) {
          const intent = await stripeChangePlan({
            uid: userUid,
            plan_name: plan.name,
            is_annual: isAnnual,
          });
          if (intent.payment_required && intent.client_secret) {
            setStripeClientSecret(intent.client_secret);
            setStripeSuccessMessage(t('planDetails.plan_change_success', { hits: intent.hits }));
            setIsPaymentLoading(false);
            return;
          }
          toast.success(t('planDetails.plan_change_success', { hits: intent.hits }));
          setIsPaymentLoading(false);
          navigate(
            `/payment-success?flow=plan_update&plan=${encodeURIComponent(plan.name)}`
          );
          return;
        }

        toast.info(t('planDetails.already_subscribed'));
        setIsPaymentLoading(false);
        navigate('/pricing');
        return;
      }

      if (!isRazorpayLoaded || !window.Razorpay) {
        toast.error(t('planDetails.razorpay_not_loaded'));
        setIsPaymentLoading(false);
        return;
      }

      let orderResponse={order_id:"",amount:0,currency:""}
      if (plan.name === 'Top-Up') {
        trackEvent('payment_confirm_clicked', {
          category: 'Checkout',
          gateway: 'razorpay',
          plan_name: plan.name,
          billing_period: billingPeriod,
          device_type: deviceType,
          flow_type: flowType,
        });

        if(currencyState=='USD'){
         orderResponse = await createOrder({
          amount:399, // 3.99 USD (399 cents)
          currency: 'USD',
          uid: userUid,
        });
      }else{
         orderResponse = await createOrder({
          amount: 29400, // ₹294 INR (29,400 paise)
          currency: 'INR',
          uid: userUid,
        });
      }
        const { order_id, amount, currency } = orderResponse;

        const options: RazorpayOptions = {
          key: import.meta.env.VITE_REACT_APP_RAZORPAY_KEY_ID as string,
          order_id,
          amount,
          currency,
          name: 'Sarvalok',
          description: t('planDetails.topup_description'),
          handler: async (response) => {
            const verification = await verifyOrder({
              razorpay_order_id: response.razorpay_order_id || '',
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              uid: userUid,
            });
            if (verification.status === 'success') {
              const purchaseValue = getValueFromMinorUnits(amount);
              trackPurchase({
                gateway: 'razorpay',
                transactionId: response.razorpay_payment_id,
                currency,
                value: purchaseValue,
                planName: plan.name,
                billingPeriod,
                flowType,
              });
              trackEvent('payment_success', {
                category: 'Checkout',
                gateway: 'razorpay',
                plan_name: plan.name,
                billing_period: billingPeriod,
                device_type: deviceType,
                flow_type: flowType,
                currency,
                value: purchaseValue,
                transaction_id: response.razorpay_payment_id,
              });
              try {
                localStorage.setItem(
                  'sarvalok_last_purchase',
                  JSON.stringify({
                    gateway: 'razorpay',
                    flow_type: flowType,
                    plan_name: plan.name,
                    billing_period: billingPeriod,
                    currency,
                    value: purchaseValue,
                    transaction_id: response.razorpay_payment_id,
                    ts: Date.now(),
                  })
                );
              } catch {}
              toast.success(t('planDetails.topup_success', { hits: formatHits(razorpayTopupHits) }));
              navigate(
                `/payment-success?gateway=razorpay&flow=${encodeURIComponent(flowType)}&plan=${encodeURIComponent(
                  plan.name
                )}&billing=${encodeURIComponent(billingPeriod)}`
              );
            } else {
              trackEvent('payment_error', {
                category: 'Checkout',
                gateway: 'razorpay',
                plan_name: plan.name,
                billing_period: billingPeriod,
                device_type: deviceType,
                flow_type: flowType,
                currency,
                value: getValueFromMinorUnits(amount),
                error_message: 'topup_verification_failed',
              });
              toast.error(t('planDetails.topup_verification_failed'));
              setIsPaymentLoading(false);
            }
          },
          theme: { color: '#000' },
          modal: {
            ondismiss: () => {
              trackEvent('checkout_abandoned', {
                category: 'Checkout',
                gateway: 'razorpay',
                plan_name: plan.name,
                billing_period: billingPeriod,
                device_type: deviceType,
                flow_type: flowType,
                currency,
                value: getValueFromMinorUnits(amount),
              });
              toast.warn(t('planDetails.payment_window_closed'));
              setIsPaymentLoading(false);
            },
          },
          retry: { enabled: true, max_count: 3 },
        };

        const razorpay = new window.Razorpay(options);
        razorpay.on('payment.failed', (response: any) => {
          trackEvent('payment_error', {
            category: 'Checkout',
            gateway: 'razorpay',
            plan_name: plan.name,
            billing_period: billingPeriod,
            device_type: deviceType,
            flow_type: flowType,
            currency,
            value: getValueFromMinorUnits(amount),
            error_message: response?.error?.description || 'payment_failed',
          });
          toast.error(t('planDetails.payment_failed', { description: response.error.description }));
          const retry = window.confirm(t('planDetails.retry_payment'));
          if (retry) razorpay.open();
          else setIsPaymentLoading(false);
        });
        razorpay.open();
      } else {
        trackEvent('payment_confirm_clicked', {
          category: 'Checkout',
          gateway: 'razorpay',
          plan_name: plan.name,
          billing_period: billingPeriod,
          device_type: deviceType,
          flow_type: flowType,
        });

        const planId = isAnnual ? plan.yearlyPlanId : plan.monthlyPlanId;
        if (!planId) {
          toast.error(t('planDetails.invalid_plan'));
          setIsPaymentLoading(false);
          return;
        }

        const isExistingPlanChange = Boolean(
          currentSubscription.subscriptionId &&
            !currentSubscription.isExpired &&
            (currentSubscription.planName !== plan.name ||
              currentSubscription.period !== (isAnnual ? 12 : 1))
        );

        if (isExistingPlanChange) {
          // Razorpay applies an immediate subscription update itself, including
          // any required prorated charge/refund. It does not return a new Checkout
          // subscription, so opening Razorpay Checkout again here would pass
          // undefined amount/currency/id values and ask the user to pay twice.
          const changePlanResponse = await changePlan({
            subscription_id: currentSubscription.subscriptionId!,
            period: isAnnual ? 12 : 1,
            new_plan_id: planId,
            new_plan_name: plan.name,
            uid: userUid,
          });
          if (changePlanResponse?.status !== 'success') {
            throw new Error(t('planDetails.payment_verification_failed'));
          }

          // A previous checkout receipt must not be attributed to this provider-
          // managed update when PaymentSuccessPage mounts.
          try {
            localStorage.removeItem('sarvalok_last_purchase');
          } catch {
            // Storage can be unavailable in restricted/private browser contexts.
          }
          trackEvent('plan_change_success', {
            category: 'Checkout',
            gateway: 'razorpay',
            plan_name: plan.name,
            billing_period: billingPeriod,
            device_type: deviceType,
            flow_type: flowType,
          });
          toast.success(t('planDetails.plan_change_success', { hits: changePlanResponse.hits }));
          navigate(
            `/payment-success?gateway=razorpay&flow=${encodeURIComponent(flowType)}&plan=${encodeURIComponent(
              plan.name
            )}&billing=${encodeURIComponent(billingPeriod)}`
          );
          return;
        }

        if (
          !currentSubscription.subscriptionId ||
          currentSubscription.isExpired ||
          currentSubscription.planName === 'free_plan'
        ) {
          // New subscription
          const subscriptionResponse = await createSubscription({
            plan_id: planId,
            customer_name: userDetails.name,
            customer_email: userDetails.email,
            customer_contact: userDetails.contact,
            uid: userUid,
            is_annual: isAnnual, // Pass this flag to the backend
          });

          const subscription_id = subscriptionResponse.subscription_id;
          const amount = subscriptionResponse.amount;
          const currency = subscriptionResponse.currency;

          const options: RazorpayOptions = {
            key: import.meta.env.VITE_REACT_APP_RAZORPAY_KEY_ID as string,
            subscription_id,
            amount,
            currency,
            name: 'Sarvalok',
            description: `${plan.name} Subscription`,
            handler: async (response) => {
              const verification = await verifyPayment({
                razorpay_subscription_id: response.razorpay_subscription_id || '',
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                subscription_name: plan.name,
                uid: userUid,
              });
              if (verification.status === 'success') {
                const purchaseValue = getValueFromMinorUnits(amount);
                trackPurchase({
                  gateway: 'razorpay',
                  transactionId: response.razorpay_payment_id,
                  currency,
                  value: purchaseValue,
                  planName: plan.name,
                  billingPeriod,
                  flowType,
                });
                trackEvent('payment_success', {
                  category: 'Checkout',
                  gateway: 'razorpay',
                  plan_name: plan.name,
                  billing_period: billingPeriod,
                  device_type: deviceType,
                  flow_type: flowType,
                  currency,
                  value: purchaseValue,
                  transaction_id: response.razorpay_payment_id,
                });
                try {
                  localStorage.setItem(
                    'sarvalok_last_purchase',
                    JSON.stringify({
                      gateway: 'razorpay',
                      flow_type: flowType,
                      plan_name: plan.name,
                      billing_period: billingPeriod,
                      currency,
                      value: purchaseValue,
                      transaction_id: response.razorpay_payment_id,
                      ts: Date.now(),
                    })
                  );
                } catch {}
                toast.success(t('planDetails.subscription_success'));
                navigate(
                  `/payment-success?gateway=razorpay&flow=${encodeURIComponent(flowType)}&plan=${encodeURIComponent(
                    plan.name
                  )}&billing=${encodeURIComponent(billingPeriod)}`
                );
              } else {
                trackEvent('payment_error', {
                  category: 'Checkout',
                  gateway: 'razorpay',
                  plan_name: plan.name,
                  billing_period: billingPeriod,
                  device_type: deviceType,
                  flow_type: flowType,
                  currency,
                  value: getValueFromMinorUnits(amount),
                  error_message: 'payment_verification_failed',
                });
                toast.error(t('planDetails.payment_verification_failed'));
                setIsPaymentLoading(false);
              }
            },
            theme: { color: '#000' },
            modal: {
              ondismiss: () => {
                trackEvent('checkout_abandoned', {
                  category: 'Checkout',
                  gateway: 'razorpay',
                  plan_name: plan.name,
                  billing_period: billingPeriod,
                  device_type: deviceType,
                  flow_type: flowType,
                  currency,
                  value: getValueFromMinorUnits(amount),
                });
                toast.warn('Payment window closed. Please try again.');
                setIsPaymentLoading(false);
              },
            },
            retry: { enabled: true, max_count: 3 },
          };

          const razorpay = new window.Razorpay(options);
          razorpay.on('payment.failed', (response: any) => {
            trackEvent('payment_error', {
              category: 'Checkout',
              gateway: 'razorpay',
              plan_name: plan.name,
              billing_period: billingPeriod,
              device_type: deviceType,
              flow_type: flowType,
              currency,
              value: getValueFromMinorUnits(amount),
              error_message: response?.error?.description || 'payment_failed',
            });
            toast.error(`Payment failed: ${response.error.description}`);
            const retry = window.confirm('Would you like to retry?');
            if (retry) razorpay.open();
            else setIsPaymentLoading(false);
          });
          razorpay.open();
          return;
        } else {
          toast.info(t('planDetails.already_subscribed'));
          setIsPaymentLoading(false);
          navigate('/pricing');
          return;
        }
      }
    } catch (error: any) {
      // Improved error message display
      toast.error(`Error: ${error.detail || error.message || 'An unknown error occurred.'}`);
      setIsPaymentLoading(false);
    }
  };

  const checkSubscriptionStatus = async (subscriptionId: string) => {
    const response = await getSubscriptionStatus(subscriptionId);
    const { status, is_expired, remaining_count } = response;
    toast.info(t('planDetails.subscription_status_info', { status, expired: is_expired, remaining: remaining_count }));
  };

  // Navigate explicitly to localized pricing to avoid legacy redirect loops
  const handleBack = () => {
    navigate('/pricing');
  };

  const deviceType: 'mobile' | 'desktop' =
    typeof window !== 'undefined' && window.innerWidth < 640 ? 'mobile' : 'desktop';
  const billingPeriod: 'monthly' | 'annual' = isAnnual ? 'annual' : 'monthly';
  const purchaseCurrency = currencyState || 'USD';
  const purchaseValue = (() => {
    const raw = plan.name === 'Top-Up' ? plan.monthlyPrice : isAnnual ? plan.yearlyPrice : plan.monthlyPrice;
    const normalized = String(raw || '')
      .replace(/[^0-9.,-]/g, '')
      .replace(/,/g, '');
    const n = Number.parseFloat(normalized);
    return Number.isFinite(n) && n > 0 ? n : undefined;
  })();
  const isPlanChange = Boolean(
    currentSubscription.subscriptionId &&
      !currentSubscription.isExpired &&
      (currentSubscription.planName !== plan.name || currentSubscription.period !== (isAnnual ? 12 : 1))
  );
  const flowType: 'subscribe' | 'topup' | 'plan_change' =
    plan.name === 'Top-Up' ? 'topup' : isPlanChange ? 'plan_change' : 'subscribe';

  useEffect(() => {
    if (!stripeClientSecret || !stripeFormRef.current) return;
    const el = stripeFormRef.current;
    const observer = new ResizeObserver(() => {
      if (el.offsetHeight > 100) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        observer.disconnect();
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [stripeClientSecret]);

  const stripeAppearance = isDark ? {
    theme: 'night' as const,
    variables: {
      colorBackground: '#1f2937',
      colorText: '#e5e7eb',
      colorTextSecondary: '#9ca3af',
      colorPrimary: '#3b82f6',
      colorDanger: '#ef4444',
      fontFamily: "'General Sans', system-ui, -apple-system, 'Segoe UI', sans-serif",
      borderRadius: '10px',
      spacingUnit: '5px',
    },
    rules: {
      '.Input': {
        border: '1px solid #4b5563',
        boxShadow: 'none',
        backgroundColor: '#374151',
        padding: '10px 12px',
        color: '#e5e7eb',
      },
      '.Input:focus': {
        border: '1px solid #3b82f6',
        boxShadow: '0 0 0 1px #3b82f6',
      },
      '.Tab': {
        border: '1px solid #4b5563',
        backgroundColor: '#1f2937',
      },
      '.Tab--selected': {
        border: '1px solid #3b82f6',
        backgroundColor: '#1e3a8a',
      },
      '.Label': {
        color: '#d1d5db',
      },
    },
  } : {
    theme: 'stripe' as const,
    variables: {
      colorBackground: '#f9fafb',
      colorText: '#111827',
      colorTextSecondary: '#4b5563',
      colorPrimary: '#2563eb',
      colorDanger: '#dc2626',
      fontFamily: "'General Sans', system-ui, -apple-system, 'Segoe UI', sans-serif",
      borderRadius: '10px',
      spacingUnit: '5px',
    },
    rules: {
      '.Input': {
        border: '1px solid #d1d5db',
        boxShadow: 'none',
        backgroundColor: '#f9fafb',
        padding: '10px 12px',
      },
      '.Input:focus': {
        border: '1px solid #2563eb',
        boxShadow: '0 0 0 1px #2563eb',
      },
      '.Tab': {
        border: '1px solid #d1d5db',
        backgroundColor: '#ffffff',
      },
      '.Tab--selected': {
        border: '1px solid #2563eb',
        backgroundColor: '#eff6ff',
      },
    },
  };

  return (
    <div className="bg-white dark:bg-gray-950 py-24">
      <div className="container mx-auto px-6">
        {/* FINAL DYNAMIC HEADER */}
        <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-8 text-center">
          {isSwitchingToAnnual
            ? t('planDetails.switch_to_annual', { planName: plan.name })
            : isSwitchingToMonthly
            ? t('planDetails.switch_to_monthly', { planName: plan.name })
            : t('planDetails.your_selected_plan', { planName: plan.name === "free_plan" ? t('planDetails.free_plan_display') : plan.name })
          }
        </h2>
        
        {/* FINAL DYNAMIC MESSAGING */}
        {currentSubscription.subscriptionId && (
          <p className="text-center text-gray-700 dark:text-gray-300 mb-4">
            {currentSubscription.isExpired ? (
              t('planDetails.subscription_expired')
            ) : isSwitchingToAnnual ? (
              <span className="font-semibold text-green-600">{t('planDetails.save_annual')}</span>
            ) : isSwitchingToMonthly ? (
              <span className="font-semibold text-blue-600">{t('planDetails.switch_monthly_flexibility')}</span>
            ) : !isPlanNameSame ? (
               <>
                 {t('planDetails.changing_from_to', { 
                   fromPlan: currentSubscription.planName === 'free_plan' ? t('planDetails.free_plan_display') : `${currentSubscription.planName} ${currentSubscription.period === 12 ? t('planDetails.annual') : t('planDetails.monthly')}`,
                   toPlan: plan.name
                 })}
               </>
            ) : (
              // This only appears if both plan name AND period are identical
              t('planDetails.already_subscribed')
            )}
          </p>
        )}
        <div className="max-w-2xl mx-auto bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-2xl p-8">
          <div className="flex flex-col items-start mb-8">
            <div className="flex items-baseline">
              <span className={`${plan.name === 'Enlightened' ? 'text-3xl' : 'text-4xl'} font-bold text-gray-900 dark:text-gray-100`}>
                {getPlanDisplayPrice(plan)}
              </span>
              {getPlanDisplayPeriod(plan.name) ? (
                <span className="ml-2 text-gray-500 dark:text-gray-400">{getPlanDisplayPeriod(plan.name)}</span>
              ) : null}
            </div>
            {currencyState === 'INR' && plan.name !== 'free_plan' && (
              <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                {tp('pricing.base_price_gst_note')}
              </div>
            )}
            {plan.name !== 'free_plan' && plan.name !== 'Enlightened' && plan.name !== 'Top-Up' && (
              <p className="text-xs font-medium mt-1 mb-0 text-amber-600">
                {tp('pricing.launch_offer_limited_period')}
              </p>
            )}
          </div>
          <ul className="space-y-5 mb-10">
            {getTranslatedFeatures(plan.name).map((feature, index) => (
              <li key={index} className="flex items-center">
                <Check className="w-5 h-5 mr-3 text-green-500" />
                <span className="text-gray-800 dark:text-gray-200">{feature}</span>
              </li>
            ))}
          </ul>
          <MissionBadgeCompact
            className="mb-6"
            title={t('planDetails.mission_badge_title')}
            description={t('planDetails.mission_badge_description')}
          />
          {stripeClientSecret && (
            <div ref={stripeFormRef} className="mb-6" style={{ scrollMarginTop: '80px' }}>
              <Elements stripe={stripePromise} options={{ clientSecret: stripeClientSecret, appearance: stripeAppearance }}>
                <StripePaymentForm
                  clientSecret={stripeClientSecret}
                  onDone={(result) => {
                    if (!result.ok) {
                      toast.error(result.message || 'Payment failed');
                      return;
                    }
                    const paymentStatus = result.status || 'succeeded';
                    const paymentStatusQuery = `&payment_status=${encodeURIComponent(paymentStatus)}`;
                    const paymentIntentQuery = result.paymentIntentId
                      ? `&payment_intent=${encodeURIComponent(result.paymentIntentId)}`
                      : '';
                    if (paymentStatus === 'processing') {
                      toast.info(
                        flowType === 'topup'
                          ? t('planDetails.topup_processing', {
                              defaultValue: 'Your bank payment is processing. Credits will be added once Stripe confirms it.',
                            })
                          : t('planDetails.stripe_payment_processing', {
                              defaultValue: 'Your payment is processing. Access will update once Stripe confirms it.',
                            })
                      );
                    } else {
                      toast.success(stripeSuccessMessage || t('planDetails.subscription_success'));
                    }
                    navigate(
                      `/payment-success?gateway=stripe&flow=${encodeURIComponent(flowType)}&plan=${encodeURIComponent(
                        plan.name
                      )}&billing=${encodeURIComponent(billingPeriod)}${paymentStatusQuery}${paymentIntentQuery}`
                    );
                  }}
                  planName={plan.name}
                  billingPeriod={billingPeriod}
                  deviceType={deviceType}
                  flowType={flowType}
                  purchaseCurrency={purchaseCurrency}
                  purchaseValue={purchaseValue}
                  userEmail={userDetails.email}
                />
              </Elements>
            </div>
          )}
          {!stripeClientSecret && (
            <button
              onClick={handlePayment}
              disabled={isPaymentLoading || !userDetails.email}
              className={`w-full py-3 px-6 rounded-lg font-medium transition flex items-center justify-center ${
                isPaymentLoading || !userDetails.email
                  ? 'bg-gray-400 dark:bg-gray-700 text-gray-700 dark:text-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900'
              }`}
            >
              {isPaymentLoading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  {t('planDetails.processing')}
                </>
              ) : !userDetails.email ? (
                t('planDetails.loading_user_details')
              ) : currentSubscription.subscriptionId &&
                !currentSubscription.isExpired &&
                currentSubscription.planName !== plan.name ? (
                  plan.name=="Top-Up" ? t('planDetails.pay_for_topup') :
                t('planDetails.upgrade_plan')
              ) : (
                t('planDetails.continue_to_pay')
              )}
            </button>
          )}
          <button
            onClick={handleBack}
            // Removed disabled={isNavigatingBack} and its related logic from here
            className={`w-full mt-4 py-3 px-6 rounded-lg font-medium transition flex items-center justify-center ${
                 'bg-gray-200 dark:bg-gray-800 text-gray-900 dark:text-gray-100 hover:bg-gray-300 dark:hover:bg-gray-700'
            }`}
          >
            {/* Removed isNavigatingBack condition for text as well */}
            {t('planDetails.back_to_plans')}
          </button>
        </div>
      </div>
      <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} />
    </div>
  );
}
